import { useSyncExternalStore } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "taskmgmt-theme";

/**
 * Theme preference, persisted per browser.
 *
 * <p>Deliberately a real choice rather than following the operating system alone. People work in
 * rooms whose lighting does not match their machine's schedule, and a preference that resets on
 * every reload is not a preference.
 *
 * <p>Implemented against `useSyncExternalStore` rather than Context so the toggle does not
 * re-render the entire application tree, and so `applyTheme` can run before React mounts.
 */

function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle("dark", theme === "dark");
}

function readStored(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return stored;
    }
    // No stored choice: follow the OS the first time, then remember whatever they pick.
    if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
  } catch {
    // Private browsing and blocked storage both throw here. A missing preference is not worth
    // failing over — fall through to the light default.
  }
  return "light";
}

let current: Theme = readStored();
const listeners = new Set<() => void>();

/** Applied immediately, before React mounts, so the first paint is already the right theme. */
applyTheme(current);

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setTheme(theme: Theme): void {
  current = theme;
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // The theme still applies for this session even if it cannot be remembered.
  }
  applyTheme(theme);
  listeners.forEach((listener) => listener());
}

export function toggleTheme(): void {
  setTheme(current === "dark" ? "light" : "dark");
}

export function useTheme(): Theme {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => "light" as Theme,
  );
}
